import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { TaskListQuery } from "@pkhoffice/shared";
import {
  fetchTasks,
  fetchTask,
  fetchMyTasks,
  createTask,
  updateTask,
  deleteTask,
  completeTask,
} from "./api";

export function useTasks(query: Partial<TaskListQuery>) {
  return useQuery({
    queryKey: ["tasks", query],
    queryFn: () => fetchTasks(query),
    staleTime: 30_000,
  });
}

export function useTask(id: number | undefined) {
  return useQuery({
    queryKey: ["task", id],
    queryFn: () => fetchTask(id!),
    enabled: id !== undefined && id > 0,
  });
}

export function useMyTasks() {
  return useQuery({ queryKey: ["tasks", "my"], queryFn: fetchMyTasks, staleTime: 30_000 });
}

export function useCreateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createTask,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tasks"] }),
  });
}

export function useUpdateTask(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) => updateTask(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["task", id] });
      qc.invalidateQueries({ queryKey: ["tasks"] });
    },
  });
}

export function useDeleteTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteTask,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tasks"] }),
  });
}

export function useCompleteTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      percent,
      completed,
    }: {
      id: number;
      percent?: number;
      completed?: boolean;
    }) => completeTask(id, percent, completed),
    onSuccess: (_, v) => {
      qc.invalidateQueries({ queryKey: ["task", v.id] });
      qc.invalidateQueries({ queryKey: ["tasks"] });
    },
  });
}
